import { useSelector, useDispatch } from 'react-redux';
import { format } from 'date-fns';
import { styled } from '@mui/material/styles';
import TableContainer from '@mui/material/TableContainer';
import DeleteIcon from '@mui/icons-material/Delete';
import IconButton from '@mui/material/IconButton';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Table } from '@components/Table';
import { Typography } from '@components/Typography';
import Tooltip from '@components/Tooltip';
import { getChannelIcon } from '@helpers/channels';
import { LabelPlain } from '@components/Label';
import { useTranslation, useHasAccessToAsset, useChannels } from '@hooks';
import { I18N_DATE_FORMAT } from '@helpers/dates';
import { CAMPAIGN_STATUS, CAMPAIGN_TYPE } from '@constants/campaigns';
import { RIGHTS } from '@constants/user';
import { CHANNEL } from '@constants/channels';
import { campaignsActions } from '@actions';

const StyledTable = styled(Table)({
    '& .MuiTableBody-root .MuiTableCell-body:last-child': {
        padding: 4,
    },
});

const GetTableHeader = () => {
    const { t } = useTranslation();
    return [
        {
            key: 0,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.title')}
                </Typography>
            ),
        },
        {
            key: 1,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.location')}
                </Typography>
            ),
        },
        {
            key: 2,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.channel')}
                </Typography>
            ),
        },
        {
            key: 3,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('CampaignsPage.audience')}
                </Typography>
            ),
        },
        {
            key: 4,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.scheduledTime')}
                </Typography>
            ),
        },
        {
            key: 5,
            component: (
                <Typography align="left" variant="subtitle2">
                    {t('common.status')}
                </Typography>
            ),
        },
        { key: 6, component: <Typography align="left" variant="subtitle2" /> },
    ];
};

const GetTableBody = (data, language, companies, channels, brands, onClick) => {
    const { t } = useTranslation();
    const { hasAccessToAssetById } = useHasAccessToAsset();
    const { channelIsAssignedToBrandOrLocation } = useChannels();
    return data.map(campaign => {
        const rights = hasAccessToAssetById(campaign);

        const locationName = campaign.locationId
            ? companies?.find(company => company.companyId === campaign.locationId)?.internalName
            : brands?.find(brand => brand.id === campaign.brandId)?.title;

        const isArchived = campaign.channelId === CHANNEL.ARCHIVED;
        const isChannelAssignedToBrandOrLocation = channelIsAssignedToBrandOrLocation(
            campaign.channelId,
            campaign.brandId,
            campaign.locationId,
        );
        let channel;
        if (isArchived) {
            channel = { type: CHANNEL.ARCHIVED, name: t('common.archived') };
        } else if (!isChannelAssignedToBrandOrLocation) {
            channel = { type: '', name: '' };
        } else {
            channel = channels.find(c => c.id === campaign.channelId);
        }
        return [
            {
                component: (
                    <Typography
                        variant="subtitle2"
                        align="left"
                        sx={{ width: 120, overflow: 'hidden', textOverflow: 'ellipsis' }}
                    >
                        {campaign.title}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left" sx={{ width: '120px' }}>
                        {locationName || '--'}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left" sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <span style={{ width: 16 }}>{getChannelIcon(channel?.type)}</span> {channel?.name}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {campaign.labels?.length ? (
                            <Tooltip placement="top" title={campaign.labels.map(l => l.name).join(', ')}>
                                <span style={{ width: 120, overflow: 'hidden', display: 'flex' }}>
                                    {campaign.labels.map(label => (
                                        <LabelPlain key={label.id} type={label.name} variant="caption">
                                            {label.name}
                                        </LabelPlain>
                                    ))}
                                </span>
                            </Tooltip>
                        ) : (
                            '--'
                        )}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left" sx={{ width: 90 }}>
                        {format(new Date(campaign.scheduledTime), 'dd MMM yyyy, HH:mm', {
                            locale: I18N_DATE_FORMAT[language],
                        })}
                    </Typography>
                ),
            },
            {
                component: (
                    <Typography variant="body2" align="left">
                        {t(campaign.isFailed ? 'common.failed' : `CampaignsPage.status${campaign.status}`)}
                        <span style={{ color: 'rgba(0, 0, 0, 0.6)' }}>
                            {' '}
                            ({campaign.progress}/{campaign.itemsCount})
                        </span>
                    </Typography>
                ),
            },
            {
                component: (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                        <Tooltip title={t('common.view')} placement="top">
                            <IconButton onClick={() => onClick(campaign, 'view')}>
                                <VisibilityIcon viewBox="0 0 22 22" />
                            </IconButton>
                        </Tooltip>
                        {(campaign.status === CAMPAIGN_STATUS.SCHEDULED || campaign.isFailed) &&
                            rights[RIGHTS.WRITE] && (
                                <Tooltip title={t('common.edit')} placement="top">
                                    <IconButton onClick={() => onClick(campaign, 'edit')}>
                                        <EditIcon viewBox="0 0 22 22" />
                                    </IconButton>
                                </Tooltip>
                            )}
                        {(campaign.status === CAMPAIGN_STATUS.SCHEDULED ||
                            campaign.status === CAMPAIGN_STATUS.INPROGRESS ||
                            campaign.isFailed) &&
                            rights[RIGHTS.WRITE] && (
                                <Tooltip title={t('common.delete')} placement="top">
                                    <IconButton onClick={() => onClick(campaign, 'delete')}>
                                        <DeleteIcon viewBox="0 0 22 22" />
                                    </IconButton>
                                </Tooltip>
                            )}
                    </div>
                ),
            },
        ];
    });
};

const OneTimeTable = ({ companies, brands, rowsPerPage, page, setPage, setRowsPerPage, onClickCampaign }) => {
    const { t } = useTranslation();
    const { data, loading, totalCount, pages } = useSelector(state => state.campaigns);
    const { data: channels } = useSelector(state => state.channels);
    const language = useSelector(state => state.auth.user?.language);
    const dispatch = useDispatch();

    const onPageChange = (_, newPage) => {
        setPage(newPage);
        dispatch(campaignsActions.get(newPage, rowsPerPage, CAMPAIGN_TYPE.ONETIME));
    };

    const onRowsPerPageChange = event => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
        dispatch(campaignsActions.get(0, parseInt(event.target.value, 10), CAMPAIGN_TYPE.ONETIME));
    };

    return (
        <TableContainer>
            <StyledTable
                withHover
                columnNames={GetTableHeader()}
                rows={GetTableBody(data, language, companies, channels, brands, onClickCampaign)}
                isDataLoading={loading}
                withPagination
                paginationProps={{
                    colSpan: 7,
                    count: totalCount,
                    rowsPerPage,
                    page,
                    onPageChange,
                    onRowsPerPageChange,
                    labelDisplayedRows: ({ from, to, count }) =>
                        `${from}–${to} ${t('Table.of')} ${count !== -1 ? count : `${t('Table.moreThan')} ${to}`}`,
                    labelRowsPerPage: <span>{t('Table.rowsPerPage')}</span>,
                    pages,
                }}
            />
        </TableContainer>
    );
};

export default OneTimeTable;
